// Server state. Every GET is cached under its URL, so two screens asking for the same thing share one request,
// and a tab you just left comes back instantly. Screens read with useData/useAll (Suspense: data is always there);
// every write goes through run(), which refreshes whatever is on screen.
import { QueryClient, useSuspenseQueries, useSuspenseQuery } from "@tanstack/react-query";
import { ApiError, SignedOut, api } from "./api";
import { toast } from "../ui/toast";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (n, e) => !(e instanceof ApiError) && n < 1, // a network blip gets one retry; a 4xx is an answer
    },
  },
});

export function useData<T>(path: string, opts: { refetchInterval?: number } = {}): T {
  return useSuspenseQuery({ queryKey: [path], queryFn: () => api<T>(path), ...opts }).data;
}

/** A request whose failure shouldn't take the screen down: it resolves to `fallback` instead. */
export const optional = <T>(path: string | null, fallback: T) => ({ path, fallback });
type Q = string | null | { path: string | null; fallback: unknown };

/** Several requests in parallel (useData one after another would fetch them one by one). null = skip (null). */
export function useAll<T extends unknown[]>(...qs: { [K in keyof T]: Q }): T {
  return useSuspenseQueries({
    queries: qs.map((q) => {
      const opt = typeof q === "object" && q !== null;
      const path = opt ? q.path : q;
      return {
        queryKey: opt ? [path, "optional"] : [path], // required ones share useData's cache
        queryFn: () => (path ? api(path).catch((e) => { if (!opt || e instanceof SignedOut) throw e; return q.fallback; }) : Promise.resolve(null)),
      };
    }),
  }).map((r) => r.data) as T;
}

/** An error already shown to the user (a toast); callers just stop. Never reported as unhandled. */
export class Handled extends Error {}
addEventListener("unhandledrejection", (e) => { if (e.reason instanceof Handled) e.preventDefault(); });

// The button that set a write going (clicked, or the submit button of the form Enter was pressed in) shows a spinner
// and ignores more presses until the write is done: no double quotes, no duplicate leads. Pressed buttons are tracked
// on pointerdown because Safari doesn't focus a button when it's clicked.
let pressed: { el: HTMLButtonElement; at: number } | null = null;
addEventListener("pointerdown", (e) => {
  const el = (e.target as Element | null)?.closest?.("button");
  pressed = el ? { el, at: Date.now() } : null;
}, true);
function busyButton(): HTMLButtonElement | null {
  if (pressed && Date.now() - pressed.at < 2000 && pressed.el.isConnected) return pressed.el;
  const a = document.activeElement;
  if (a instanceof HTMLButtonElement) return a;
  return a?.closest("form")?.querySelector<HTMLButtonElement>("button:not([type=button])") ?? null;
}

/** Every write: toast the outcome, then refresh what is on screen. Throws Handled on failure, so `await run(…);
 * closeModal()` stops at the error without further checks. */
export async function run<T>(fn: () => Promise<T>, ok?: string): Promise<T> {
  const btn = busyButton();
  if (btn?.getAttribute("aria-busy") === "true") throw new Handled("already saving"); // a second press
  btn?.setAttribute("aria-busy", "true");
  pressed = null;
  let r: T;
  try {
    r = await fn();
  } catch (e) {
    if (!(e instanceof SignedOut)) toast((e as Error).message);
    throw new Handled((e as Error).message);
  } finally {
    btn?.removeAttribute("aria-busy");
  }
  if (ok) toast(ok);
  void queryClient.invalidateQueries();
  return r;
}

/** Refresh what is on screen (the legacy render()). */
export const refresh = () => queryClient.invalidateQueries();
